import { mediaUrl } from "@/lib/note";
import type { ReviewCard } from "@/lib/review";

import {
  answerLayout,
  partOfSpeechLine,
  splitOnTarget,
  tagChip,
  visibleImages,
  type ImageMode,
} from "./sessionRules";

/**
 * The card in the middle of the screen — question and answer, frames `5f`, `6a`/`6b`, `7b`, `7c`.
 *
 * **The question is the Japanese field and nothing else.** No reading, no deck name, no tags: every
 * one of those is a hint, and a reviewer that leaks the answer into the question is not measuring
 * anything. That is why `revealed` gates whole blocks rather than styling them.
 */

interface Props {
  card: ReviewCard;
  revealed: boolean;
  isPhone: boolean;
  imageMode: ImageMode;
  onReveal: () => void;
  onPlay: (filename: string) => void;
  /** The filename currently sounding, or null. Drawn as an on state on whichever button started it
   *  — see the audio row below. */
  playing: string | null;
  /** Open a picture in the lightbox (日本-350). Absent means the images are not clickable, which is
   *  what a caller with no lightbox above it gets. See `ImageSlot` for why `Session` may refuse. */
  onExpand?: (filename: string) => void;
  /*
   * **`canTap` was here and went with 日本-519.** It was `onExpand`'s predicate half, generalised
   * for the 例文 and 解説 toggles (日本-498): `Session` owns the card region's
   * `onTouchStart`/`onTouchEnd`, a sideways swipe there grades, and a browser does not reliably
   * suppress the `click` that follows a touch drag — so without asking first, every left-swipe
   * beginning on `＋4例` would open the fold *and* fail the card. Those two toggles were the only
   * in-card buttons on this face, and they left with `Grammar (Apoc)`.
   *
   * **A third one would need it back.** `Session` still holds `wasTap`, so reviving it is one prop.
   */
  /** Whether the window is past the widescreen breakpoint (日本-350).
   *
   *  Passed in rather than read here, because `Session` needs the same answer to set the measure
   *  its header and footer are capped to — two `useWideLayout()` calls could disagree for one frame
   *  while a window is being dragged, which is a footer at a different width from the card above it. */
  wide?: boolean;
}

/**
 * The headword steps up a size when there is nothing else in the middle (`7b`, "No image").
 *
 * The frames give four fill states three different type sizes, and the rule behind them is that the
 * card fills its space rather than floating in it: two images at 268px, one at 268px, or none and
 * the word and sentence grow. It is a size table rather than a flex trick because the frames are
 * explicit about the numbers.
 */
function sizes(isPhone: boolean, imageCount: number, split: boolean) {
  if (isPhone) {
    return { reading: 18, headword: 58, gloss: 17, sentence: 19 };
  }
  // The widescreen face gives the word a column to itself rather than a page width shared with
  // everything else, so it can carry the design's 124px — frame `1c`'s own figures.
  if (split) {
    return { reading: 30, headword: 124, gloss: 26, sentence: 30 };
  }
  return imageCount === 0
    ? { reading: 20, headword: 84, gloss: 19, sentence: 21 }
    : { reading: 24, headword: 96, gloss: 20, sentence: 23 };
}

export function CardFace({
  card,
  revealed,
  isPhone,
  imageMode,
  onReveal,
  onPlay,
  playing,
  onExpand,
  wide = false,
}: Props) {
  /**
   * Whether the examples block is open (日本-498).
   *
   * **Per card and never remembered**, reset in a `useLayoutEffect` for exactly `revealed`'s
   * reason: `position` arrives from an awaited promise, so the next card renders while this is
   * still true, and after paint is too late — the frame in between would draw the next card's
   * examples opened at the previous card's scroll position.
   *
   * Keyed on `cardId` rather than cleared on unmount, because `CardFace` does not unmount between
   * cards.
   */
  // A notetype this app has no layout for. Alex's call, 9 Aug 2026: fail loud rather than draw a
  // blank card. The chrome, the counters and `Card ⋯` all keep working around it, so the card can
  // still be suspended and the session can still be left.
  if (card.layout === null) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-10">
        <div className="max-w-[420px] text-center">
          <div aria-hidden className="font-jp text-[46px]/[1] text-ink-off">
            札
          </div>
          <h2 className="mt-4 text-title font-semibold text-ink">
            このカードの表示形式がありません
          </h2>
          {/* The notetype names are user content and stay in the mono face — they are what you
              would type into Anki's own browser, so translating them would name something that
              does not exist. */}
          <p className="mt-2 text-secondary text-ink-3">
            復習が描けるのは <span className="font-mono">Genki (Apoc)</span> と{" "}
            <span className="font-mono">Names (Apoc)</span> のノートだけです。これは{" "}
            <span className="font-mono text-ink-2">{card.notetype}</span>{" "}
            なので、Anki デスクトップで復習します。
          </p>
          <p className="mt-3 text-meta text-ink-4">
            カード ⋯ から停止すればキューから外れます。Escで終了。
          </p>
        </div>
      </div>
    );
  }

  const images = visibleImages(card, imageMode);
  const layout = answerLayout(wide, images.length);
  const size = sizes(isPhone, images.length, layout === "split");
  const pos = partOfSpeechLine(card.partOfSpeech);
  // `leech` first: it is the only tinted chip and the only one that is about the card rather than
  // about the word, so burying it three chips deep would be the one place the ordering matters.
  const chips = card.tags.map(tagChip).sort((a, b) => Number(b.leech) - Number(a.leech));

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={onReveal}
        // The whole region reveals — the design's "or tap the card", and on a phone the button
        // below is a target of last resort rather than the way through.
        className="flex min-h-0 flex-1 cursor-pointer items-center justify-center p-10 text-center"
        // Names the CARD, not the action — the footer button is already called "Show answer", and
        // two buttons sharing an accessible name is a screen reader announcing the same control
        // twice with no way to tell them apart. Saying which word it reveals is also more useful.
        aria-label={`${card.headword} の答えを見る`}
      >
        <span
          className="font-jp text-ink"
          style={{ fontSize: isPhone ? 76 : 132, lineHeight: 1.1 }}
        >
          {card.headword}
        </span>
      </button>
    );
  }

  // **Ranged left in both layouts, and on the phone** (Alex, 16 Aug 2026).
  //
  // The widescreen face was ranged left because two centred columns read as a poster; he liked it
  // enough to want it everywhere. So this is no longer a difference between the arrangements — the
  // constant is kept rather than inlined because it is what makes that sameness legible, and it is
  // where a future second thought about the question face would go. That face stays centred: it is
  // one word alone in the region, with no column of prose under it for a left edge to line up with.
  const align = "items-start text-left";
  const reading = card.reading && (
    <span className="font-jp text-ink-3" style={{ fontSize: size.reading, lineHeight: 1 }}>
      {card.reading}
    </span>
  );
  const headword = (
    <span className="font-jp text-ink" style={{ fontSize: size.headword, lineHeight: 1.1 }}>
      {card.headword}
    </span>
  );
  const rule = <div className="h-px w-full bg-line-soft" />;
  const gloss = card.gloss && (
    <span
      className="whitespace-pre-line text-ink"
      style={{ fontSize: size.gloss, lineHeight: 1.5 }}
    >
      {card.gloss}
    </span>
  );
  {/* `7c`: part of speech is a FIELD and reads as text — it modifies the gloss, so it belongs with
      it. Tags are chips below the rule. One grey line used to do both jobs. */}
  const partOfSpeech = pos && <span className="text-secondary italic text-ink-2">{pos}</span>;
  {/* 日本-279: the card's own word, marked inside the sentence that illustrates it.

      The span comes from the NOTE (`SentenceTarget`), not from anything worked out here — a
      sentence says 躊躇った where the card says 躊躇う, and no string match crosses that. Marking
      every occurrence rather than the first is Alex's call, 23 Aug 2026.

      `--accent-text` rather than a brighter ink: `tokens.css` reserves red for "what wants you",
      and this is a word you already own — but Read has spent months using the accent to mean *the
      word you are looking at* on a selected token, and that is the sense being borrowed. Chosen
      off the design canvas as option D. */}
  const sentence = card.sentence && (
    <span
      className="whitespace-pre-line font-jp text-ink-2"
      style={{ fontSize: size.sentence, lineHeight: 1.7 }}
    >
      {splitOnTarget(card.sentence, card.sentenceTarget).map((part, index) =>
        // Odd indices are the matches — see `splitOnTarget`. The key is index-based deliberately:
        // the parts of one sentence have no identity of their own, and the list is rebuilt whenever
        // either string changes.
        index % 2 === 1 ? (
          <span key={index} data-check="review.sentence.target" className="text-accent-text">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </span>
  );

  /*
   * **The four rows `Grammar (Apoc)` ADDED were here, and 日本-519 removed them with the notetype.**
   * 接続, 例文 with its `＋N例` fold, 解説 with its own, and 関連 chips. Grammar is a reference
   * surface now (文法) and never a card, so there is nothing on any notetype this app renders that
   * would fill them.
   *
   * Recorded rather than silently deleted, because the frames still draw them and this is otherwise
   * an invisible absence — and because the shape they established is worth keeping: every one was
   * `card.<field> && (…)`, so a vocabulary or name card rendered nothing at all. That is one face
   * with optional rows rather than a second face behind a branch, which is still how `Names (Apoc)`
   * is drawn.
   */

  {/* **The on state is not decoration** (日本-347). `hover:text-ink` was the whole of the feedback
      here, and a phone has no hover — so on the device where this row is most often tapped, a press
      looked exactly like nothing happening, which is how Alex reported it. `Session.play` sets
      `playing` synchronously on the click, so the state appears on the press rather than when the
      file finishes arriving. */}
  const audio = (card.audio || card.sentenceAudio) && (
    <div className="flex items-center gap-2">
      <span aria-hidden className={playing ? "text-body text-ink-3" : "text-body text-ink-3/60"}>
        {playing ? "❙❙" : "▶"}
      </span>
      {card.audio && (
        <AudioButton
          check="word"
          label="単語"
          filename={card.audio}
          playing={playing === card.audio}
          onPlay={onPlay}
        />
      )}
      {card.audio && card.sentenceAudio && <span className="text-secondary text-ink-4">·</span>}
      {card.sentenceAudio && (
        <AudioButton
          check="sentence"
          label="文"
          filename={card.sentenceAudio}
          playing={playing === card.sentenceAudio}
          onPlay={onPlay}
        />
      )}
    </div>
  );

  const tags = chips.length > 0 && (
    <>
      {rule}
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <span
            key={chip.raw}
            className={
              chip.leech
                ? "inline-flex items-center rounded-full border border-accent-line bg-accent-tint px-2.5 py-0.5 text-secondary text-accent-hi"
                : "inline-flex items-center gap-1.5 rounded-full border border-line-soft bg-sunken px-2.5 py-0.5 text-secondary text-ink-2"
            }
          >
            {chip.namespace && (
              <span className="font-mono text-field-label text-ink-4">{chip.namespace}</span>
            )}
            {chip.value}
          </span>
        ))}
      </div>
    </>
  );

  /**
   * **The widescreen face** (日本-350, frame `1c`) — text left, images right at the column's full
   * height.
   *
   * A **sibling arm** of the centred return below rather than a set of modifiers on it, because the
   * two really are different arrangements: the images leave the text flow entirely and take a column
   * of their own, which no flex property turns the one-column version into.
   *
   * **The text is ranged left, not centred.** Two centred columns read as a poster; ranged-left text
   * against a right-hand image block reads as a card. The headword grows to 124px because it now has
   * a column to itself rather than a page width shared with everything else.
   *
   * It keeps the same outer scroller as the centred arm, deliberately: the flexbox centring trap
   * that `min-h-full` exists to solve is exactly as reachable in a two-column layout, and putting
   * the new arm inside the same box is what stops it coming back here.
   */
  if (layout === "split") {
    return (
      <div data-check="review.answer" data-layout="split" className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full w-full gap-10 px-10 pb-5 pt-[26px]">
          {/* Hooked so `check-review.mjs` can bound the image against the room the TEXT already
              demands (日本-417). Bounding it on the card region alone goes red on a correct long
              card — the image column is stretched by the row, which the text grows — and 飲む is
              already within 27px of that. Found in review before it could misfire. */}
          <div
            data-check="review.answer.text"
            className="flex min-w-0 flex-col justify-center gap-[18px]"
            style={{ flex: 1.05 }}
          >
            {reading}
            {headword}
            {rule}
            {gloss}
            {partOfSpeech}
            {sentence}
            {audio}
            {tags}
          </div>
          {/* The 頻度 block sits at the top of the image column, and the slots take whatever height
              is left, split between however many pictures the card has — one takes the whole column,
              two take half each. `object-contain` on each slot means a portrait screenshot
              letterboxes rather than crops. */}
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <FrequencyCorner rank={card.frequencyRank} isPhone={false} />
            <div className="flex min-h-0 flex-1 flex-col justify-center gap-3.5">
              {images.length === 0 ? (
                <ImagePlaceholder />
              ) : (
                images.map((name) => (
                  <ImageSlot
                    key={name}
                    filename={name}
                    onExpand={onExpand}
                    style={{ flex: 1, minHeight: 0 }}
                    // The only slot whose height is decided purely by flex, so the only one whose
                    // picture would otherwise size the column it is supposed to fit inside — 日本-417.
                    fill
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    /*
     * **Two boxes: the outer one scrolls, the inner one centres** (日本-347).
     *
     * These were one — `flex … justify-center … overflow-y-auto` — and that is the classic flexbox
     * overflow trap. Once the content is taller than the box, centring puts the first children
     * *above* the scroll origin and there is no way to reach them: you can scroll down to the tag
     * chips and never up to the reading. Alex reported it as the scroll being temperamental and as
     * the progress bar hiding the top of the card, which is what an unreachable top edge sitting
     * directly under the chrome looks like. The chrome cannot overlap anything — it is a
     * normal-flow `flex-shrink-0` row.
     *
     * `min-h-full` on the inner box is what keeps the centring when it fits: short content stretches
     * the flex box to the scroller's height and centres inside it, tall content grows past it and is
     * anchored at the top, which is where a long answer should start.
     *
     * Plain CSS rather than Tailwind 4.1's `justify-center-safe`, which does solve it: this holds
     * wherever the desktop shell's Chromium goes and needs no support table to reason about.
     */
    <div data-check="review.answer" data-layout="column" className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex min-h-full w-full flex-col items-center justify-center gap-[22px] px-[18px] py-[24px] md:px-10 md:py-8">
        <FrequencyCorner rank={card.frequencyRank} isPhone={isPhone} />
        <div className={`flex w-full max-w-[860px] flex-col gap-4 ${align}`}>
          {reading}
          {headword}
          {rule}
          {gloss}
          {partOfSpeech}
          {sentence}
          {audio}
          {/* `7b`. Two images share the width; ONE gets a fixed 380×268 box and stays centred rather
              than stretching — a lone image at double width reads as a mistake. The *kind* of image
              never moves the layout, only the count does.

              **The phone shares the width the same way, and used to overflow instead**: every image
              took `width: 100%` regardless of how many there were, so the `both` mode put two
              full-width pictures in one flex row and the second ran off the right edge. It was
              invisible to the standing no-horizontal-scroll check, because `body` hides that scroll
              — the picture was simply cut in half, which reads as a broken image rather than as a
              layout bug. `min-w-0` is the load-bearing half: a flex item's default `min-width:auto`
              refuses to shrink below its content, so `flex: 1` alone does not stop it. */}
          {images.length > 0 && (
            <div className="flex w-full gap-4">
              {images.map((name) => (
                <ImageSlot
                  key={name}
                  filename={name}
                  onExpand={onExpand}
                  style={
                    isPhone
                      ? images.length === 1
                        ? { width: "100%", maxHeight: 200 }
                        : { flex: 1, minWidth: 0, maxHeight: 200 }
                      : images.length === 1
                        ? { width: 380, height: 268 }
                        : { flex: 1, minWidth: 0, height: 268 }
                  }
                />
              ))}
            </div>
          )}
          {tags}
        </div>
      </div>
    </div>
  );
}

/**
 * One picture on the answer face, with the affordance that opens it (日本-350).
 *
 * **Clicking the image anywhere expands it; the `⤢` button is the visible affordance.** An image
 * that silently rewards a click teaches nobody it can be clicked, and a button alone would make the
 * obvious gesture — pressing the picture — do nothing.
 *
 * **`onExpand` is a predicate as well as an action**, and that is the whole of the phone story.
 * `Session` owns the card region's `onTouchStart`/`onTouchEnd` and a swipe that begins on an image
 * must still grade the card — but a browser does not reliably suppress the `click` that follows a
 * touch drag, so without asking first, every left-swipe that started on the picture would open the
 * lightbox as well as failing the card. `Session` answers false once the touch has travelled far
 * enough to be a swipe. The shape is `BottomSheet.tsx`'s `moved` flag, and the reason not to reach
 * for `preventDefault` in `onTouchEnd` instead is the one `Session.tsx` already records: gesture
 * handling on this surface fails silently rather than loudly.
 *
 * **How many pictures are on screen is the whole of 日本-395** — `追加` on a note with no extra image
 * used to draw none, and a missing image is silent by design (frame `7b`), so the `review.card.image`
 * hook counting them is the only way a check can see it.
 *
 * **No border** (Alex, 16 Aug 2026). It sat on the SLOT rather than on the picture, and with
 * `object-fit: contain` those are not the same rectangle — in the widescreen column a 16:9 image in
 * a tall slot drew a large pale box with the picture floating in the middle of it. A border cannot
 * hug the painted area, because hugging it is exactly what `contain` refuses to do, so the honest
 * fix is to let the image be its own edge.
 */
function ImageSlot({
  filename,
  onExpand,
  style,
  fill = false,
}: {
  filename: string;
  onExpand?: (filename: string) => void;
  style: React.CSSProperties;
  /**
   * **The slot's height comes from flex, and the picture must not vote on it** (日本-417).
   *
   * Set by the widescreen arm alone, and it is the whole fix for a tall image stretching the face.
   * That arm is the only call site whose slot has no definite height — `{flex: 1, minHeight: 0}`
   * against a column whose own height is indefinite while the row is being measured. A
   * percentage-basis flex item in an indefinite container falls back to its CONTENT size, so each
   * slot contributed its image's aspect-derived height: measured on a 600×1000 portrait beside a
   * 1400×900 landscape at 1920, 1333 + 14 + 514 = 1861, which took the image column to 1898 against
   * the 770 it had, the flex row to 1944 and the scroller to 1128px of overflow.
   *
   * **What that looked like was a text bug, which is why it went unreported for a week.** The row
   * grows, the TEXT column is stretched to match, and its `justify-center` then centres the words
   * inside a column twice the height of the scroller — so the headword sat two-thirds of the way
   * down, ran under the grade buttons, and the gloss, part of speech, sentence and tags were simply
   * clipped away. Nothing on screen implicated the picture.
   *
   * Taking the image out of flow makes its contribution zero, so the column is sized by the text
   * and `min-h-full` as the handoff intends ("the image column takes whatever height is left"), and
   * `object-contain` finally has a box to contain against. Measured after: 800×360 each, no
   * overflow.
   *
   * **It must NOT reach the other call site.** The column arm's four cases all carry a definite
   * `height` or a `maxHeight`, and the phone's two carry `maxHeight` and no `height` — those rely
   * on the image's intrinsic height to give the slot any size at all, so absolute positioning would
   * collapse them to nothing. Hence a prop rather than a class on the `<span>`, which both share.
   */
  fill?: boolean;
}) {
  return (
    <span className="group relative inline-flex min-w-0" style={style}>
      <img
        src={mediaUrl(filename)}
        alt=""
        data-check="review.card.image"
        onClick={() => onExpand?.(filename)}
        className={`h-full w-full rounded-[var(--r-3)] object-contain ${fill ? "absolute inset-0" : ""}`}
        // **`maxHeight` has to reach the IMAGE, not only the wrapper.** The slot's style used to sit
        // on the `<img>` itself; it now sits on the span that wraps it so the `⤢` can be positioned
        // against something. The phone's two cases are the ones that carry a `maxHeight` and no
        // `height` — and against a wrapper whose own height is `auto`, the image's `h-full` resolves
        // to `auto` too, so it draws at its full intrinsic height and simply overflows the cap.
        // Measured at 390px on a 600×1000 picture: 340×567 inside a 340×200 span, against 340×200
        // before. The desktop cases were unaffected, because they set a definite `height`.
        style={{ maxHeight: style.maxHeight, cursor: onExpand ? "zoom-in" : "default" }}
      />
      {onExpand && (
        <button
          type="button"
          aria-label="画像を拡大"
          data-check="review.card.image.expand"
          onClick={(event) => {
            event.stopPropagation();
            onExpand(filename);
          }}
          // Hidden until hover on a pointer, always there on a phone — which has no hover, so an
          // affordance that waits for one is an affordance nobody ever sees.
          className="absolute bottom-1.5 right-1.5 flex h-[26px] w-[26px] items-center justify-center rounded-[var(--r-3)] text-body text-ink-2 opacity-0 transition-opacity group-hover:opacity-100 max-md:opacity-100"
          style={{ background: "rgba(10,10,10,.72)" }}
        >
          ⤢
        </button>
      )}
    </span>
  );
}

/**
 * What the image column draws when the card has no picture (Alex, 16 Aug 2026).
 *
 * **It exists so the widescreen face can split on width alone.** The handoff's fallback put a card
 * with no images back into the single centred column, which meant the text column changed width
 * with whatever the card happened to carry — a run of cards reflowed between two shapes as their
 * pictures came and went. A placeholder holds the column open, so the layout is a property of the
 * window rather than of the card.
 *
 * **It says so out loud, which is a deliberate exception to frame `7b`'s rule** that a missing image
 * is silent. That rule is about the image-mode toggle asking for something a note does not carry —
 * where an error message mid-review would be about a card working exactly as intended. This is not
 * that: it is the card simply having no picture, on a layout that has reserved a column for one, and
 * an unexplained empty half-screen reads as something that failed to load.
 *
 * 16:9, because that is the shape a screenshot mined from a visual novel actually is.
 */
function ImagePlaceholder() {
  return (
    <div
      data-check="review.card.no-image"
      className="flex w-full items-center justify-center rounded-[var(--r-3)] border border-dashed border-line-soft"
      style={{ aspectRatio: "16 / 9" }}
    >
      <div className="flex flex-col items-center gap-2">
        <span aria-hidden className="font-jp text-[46px]/[1] text-ink-off">
          画
        </span>
        <span className="text-meta text-ink-4">画像なし</span>
      </div>
    </div>
  );
}

/**
 * The card's corpus frequency rank, top-right (日本-366).
 *
 * **In the face's own gutter, above the centred column and outside it**, so it can never push the
 * headword off centre. That is the whole reason it is a sibling of the column rather than a child:
 * a rank inside the measure would move the word every reader's eye is fixed on.
 *
 * **A card with no rank has no corner** — no dash, no zero, no placeholder. About 600 of the
 * collection's 8,800 notes are in that state (the `Freq:Not_Found` ones), which is common enough
 * that the absence has to read as normal rather than as something that failed to load. A `—` would
 * say the opposite.
 *
 * **Raw rank, thousands-separated. No band, no percentile** — the band is what the `Freq:` tag chip
 * already says, and the chip stays (Alex, 16 Aug 2026, against the handoff, which wanted it retired
 * as one fact drawn twice). Keeping both means the rankless cards still say so somewhere.
 *
 * The label is 14px rather than the handoff's 12: `styles/index.css` puts the floor there and its
 * one exception is `Rail.tsx`'s due badge, which is digits under a hard width ceiling. 頻度 is
 * kanji. The *value* is Arabic numerals and takes the design's size unchanged.
 */
function FrequencyCorner({ rank, isPhone }: { rank: number | null; isPhone: boolean }) {
  if (rank === null) return null;
  return (
    <div data-check="review.freq" className="flex w-full flex-shrink-0 justify-end">
      <div className="flex items-baseline gap-[7px]">
        <span className="font-mono text-meta uppercase tracking-[0.08em] text-ink-4">頻度</span>
        <span
          className="font-mono font-medium tabular-nums text-ink-3"
          style={{ fontSize: isPhone ? 17 : 20, lineHeight: 1 }}
        >
          {rank.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

/**
 * One `word` / `sentence` control.
 *
 * A component rather than two inline buttons because the on state is three coordinated things — the
 * weight, the colour and the `aria-pressed` — and two copies of that is two places for them to drift
 * apart. `aria-pressed` rather than a live region: this is a toggle-shaped control whose state a
 * screen reader can read on demand, and announcing every playback would be noise.
 */
function AudioButton({
  check,
  label,
  filename,
  playing,
  onPlay,
}: {
  /**
   * The `checks/` hook, given rather than derived from `label`.
   *
   * It WAS `review.audio.${label}`, which is the one thing a hook must never be: the label is copy,
   * so translating `word` to `単語` renamed the attribute to `review.audio.単語` while every
   * selector in `check-review.mjs` still said `review.audio.word`. Nothing went red — all three
   * assertions that use it are guarded by a `count()` that reads as "this card has no audio" — so
   * the one-player check and both autoplay checks silently stopped running against every
   * collection. A hook derived from a translatable string is a hook that expires the day the copy
   * lands.
   */
  check: "word" | "sentence";
  label: string;
  filename: string;
  playing: boolean;
  onPlay: (filename: string) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={playing}
      data-check={`review.audio.${check}`}
      onClick={() => onPlay(filename)}
      className={
        playing
          ? "text-secondary font-semibold text-ink-3 underline underline-offset-2"
          : "text-secondary text-ink-3 hover:text-ink"
      }
    >
      {label}
    </button>
  );
}
